import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Tooltip,
} from '@mui/material';
import { motion } from 'framer-motion';
import LaunchIcon from '@mui/icons-material/Launch';
import VerifiedIcon from '@mui/icons-material/Verified';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { alpha } from '@mui/material/styles';

const achievements = [
  {
    name: 'Top 3.3% Nationally — 96.7th Percentile',
    body: 'National Skill Competency Test (NSCT)',
    year: '2024',
    detail: 'Ranked in the top 3.3% of all participants nationwide in the NSCT — a standardised assessment of technical and analytical competency across Pakistan.',
    color: '#f59e0b',
  },
];

const certificates = [
  {
    name: 'n8n Chatbot Lab',
    issuer: 'DataCrumbs',
    year: '2025',
    file: '/Certificates/SYED WAQAR_n8n_Chatbot_Lab_certificate.png',
    category: 'AI & Automation',
    color: '#06b6d4',
  },
  {
    name: 'TensorFlow & Keras Bootcamp',
    issuer: 'OpenCV University',
    year: '2025',
    file: '/Certificates/Free Tensorflow Keras Bootcamp Certificate.pdf',
    category: 'Deep Learning',
    color: '#8b5cf6',
  },
  {
    name: 'Understanding Cloud Computing',
    issuer: 'DataCamp',
    year: '2025',
    file: '/Certificates/Cloud Computing.pdf',
    category: 'Cloud',
    color: '#10b981',
  },
  {
    name: 'AWS Concepts',
    issuer: 'DataCamp',
    year: '2025',
    file: '/Certificates/AWS Concepts.pdf',
    category: 'Cloud',
    color: '#10b981',
  },
  {
    name: 'AWS Cloud Technology & Services',
    issuer: 'DataCamp',
    year: '2025',
    file: '/Certificates/AWS Cloud Technology and Services.pdf',
    category: 'Cloud',
    color: '#10b981',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] } },
};

function Certifications() {
  const [previewCert, setPreviewCert] = useState(null);
  const isImage = (file) => /\.(png|jpe?g|webp)$/i.test(file);

  return (
    <Container id="certifications" maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography variant="overline" sx={{ color: '#6366f1', fontWeight: 700, letterSpacing: '0.18em', mb: 1, display: 'block', fontSize: '0.75rem' }}>
            CONTINUOUS LEARNING
          </Typography>
          <Typography component="h2" sx={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: { xs: '2rem', md: '2.75rem' }, letterSpacing: '-0.02em', mb: 2, background: 'linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Certifications
          </Typography>
          <Typography sx={{ color: '#64748b', maxWidth: 540, mx: 'auto', fontSize: '1.0625rem', lineHeight: 1.7 }}>
            Verified credentials demonstrating ongoing skill development
          </Typography>
        </Box>

        {/* ── Achievements ── */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography variant="overline" sx={{ color: '#f59e0b', fontWeight: 700, letterSpacing: '0.18em', mb: 3, display: 'block', fontSize: '0.72rem' }}>
            ACHIEVEMENTS
          </Typography>
          {achievements.map((ach, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} viewport={{ once: true }}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha('#f59e0b', 0.07)} 0%, ${alpha('#f97316', 0.05)} 100%)`,
                  backdropFilter: 'blur(12px)',
                  border: `1.5px solid ${alpha('#f59e0b', 0.28)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, #f59e0b, #f97316)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 }, display: 'flex', alignItems: { sm: 'center' }, gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: 2.5, background: alpha('#f59e0b', 0.12), border: `1.5px solid ${alpha('#f59e0b', 0.3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <EmojiEventsIcon sx={{ fontSize: '1.75rem', color: '#f59e0b' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.2rem' }, color: '#f8fafc', lineHeight: 1.3, mb: 0.5 }}>
                      {ach.name}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#f59e0b', mb: 1 }}>
                      {ach.body} · {ach.year}
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem', lineHeight: 1.65 }}>
                      {ach.detail}
                    </Typography>
                  </Box>
                  <Chip label="Top 3.3%" size="small" sx={{ height: 26, fontWeight: 800, fontSize: '0.75rem', background: alpha('#f59e0b', 0.12), color: '#f59e0b', border: `1px solid ${alpha('#f59e0b', 0.3)}`, flexShrink: 0 }} />
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Box>

        {/* ── Certificates heading ── */}
        <Typography variant="overline" sx={{ color: '#6366f1', fontWeight: 700, letterSpacing: '0.18em', mb: 3, display: 'block', fontSize: '0.72rem' }}>
          CERTIFICATES
        </Typography>

        {/* Certificates Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          <Grid container spacing={2.5}>
            {certificates.map((cert, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <motion.div variants={cardVariants}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      height: '100%',
                      background: 'rgba(10,18,40,0.6)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        borderColor: alpha(cert.color, 0.3),
                        transform: 'translateY(-5px)',
                        boxShadow: `0 20px 40px rgba(0,0,0,0.3), 0 0 24px ${alpha(cert.color, 0.1)}`,
                        '&::before': { opacity: 1 },
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, ${cert.color}, ${alpha(cert.color, 0.3)})`,
                        opacity: 0.5,
                        transition: 'opacity 0.3s ease',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {/* Icon + Category */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: 2, background: alpha(cert.color, 0.12), border: `1px solid ${alpha(cert.color, 0.2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <VerifiedIcon sx={{ fontSize: '1.35rem', color: cert.color }} />
                        </Box>
                        <Chip
                          label={cert.category}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, background: alpha(cert.color, 0.08), color: cert.color, border: `1px solid ${alpha(cert.color, 0.2)}`, borderRadius: 1 }}
                        />
                      </Box>

                      {/* Name */}
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f8fafc', lineHeight: 1.4, mb: 0.75, fontFamily: "'Space Grotesk', sans-serif" }}>
                        {cert.name}
                      </Typography>

                      {/* Issuer + Year */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                        <Typography sx={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 500 }}>
                          {cert.issuer}
                        </Typography>
                        <Typography sx={{ color: '#475569', fontSize: '0.8rem', fontWeight: 600 }}>
                          {cert.year}
                        </Typography>
                      </Box>

                      {/* View Certificate Button */}
                      <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        <Button
                          onClick={() => setPreviewCert(cert)}
                          size="small"
                          endIcon={<LaunchIcon sx={{ fontSize: '0.875rem' }} />}
                          sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            color: cert.color,
                            textTransform: 'none',
                            p: 0,
                            minWidth: 0,
                            background: 'none',
                            '&:hover': { background: 'none', opacity: 0.75 },
                          }}
                        >
                          View Certificate
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </motion.div>

      {/* ── Certificate Preview Modal ── */}
      <Dialog
        open={Boolean(previewCert)}
        onClose={() => setPreviewCert(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: '#0a0f1e',
            border: `1px solid ${previewCert ? alpha(previewCert.color, 0.25) : 'transparent'}`,
            overflow: 'hidden',
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        {previewCert && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 2,
                borderBottom: `1px solid ${alpha(previewCert.color, 0.15)}`,
                background: alpha(previewCert.color, 0.05),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <VerifiedIcon sx={{ color: previewCert.color, fontSize: '1.2rem' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f8fafc' }}>
                  {previewCert.name}
                </Typography>
                <Chip
                  label={previewCert.issuer}
                  size="small"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, background: alpha(previewCert.color, 0.1), color: previewCert.color, border: `1px solid ${alpha(previewCert.color, 0.25)}` }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Open in new tab">
                  <IconButton
                    component="a"
                    href={previewCert.file}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ color: '#64748b', '&:hover': { color: previewCert.color } }}
                  >
                    <OpenInNewIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton
                    onClick={() => setPreviewCert(null)}
                    size="small"
                    sx={{ color: '#64748b', '&:hover': { color: '#f8fafc' } }}
                  >
                    <CloseIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </DialogTitle>

            <DialogContent
              sx={{
                p: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#05080f',
                minHeight: { xs: 300, sm: 500, md: 640 },
              }}
            >
              {isImage(previewCert.file) ? (
                <Box
                  component="img"
                  src={previewCert.file}
                  alt={previewCert.name}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    display: 'block',
                    p: 2,
                  }}
                />
              ) : (
                <Box
                  component="iframe"
                  src={previewCert.file}
                  title={previewCert.name}
                  sx={{
                    width: '100%',
                    height: { xs: '60vh', sm: '75vh', md: '80vh' },
                    border: 'none',
                    display: 'block',
                  }}
                />
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Container>
  );
}

export default Certifications;
